import { COMPONENT_ROLES, isValidTag } from "./vocabulary";

const COMPONENT_ROLE_SET: ReadonlySet<string> = new Set<string>(COMPONENT_ROLES);

export interface Tag {
  /** The full tag, e.g. "sp-knowledge" or "knowledge-upload". */
  tag: string;
  /** The first level: the surface or page area. */
  area: string;
  /** The last level: the verb, or the destination for navigation. */
  action: string;
  depth: number;
  tag_source: "explicit";
}

/** A click on an interactive element nobody has tagged yet (spec T3). */
export interface UntaggedClick {
  tag: null;
  tag_source: "untagged";
  /** The joined ancestor `data-ph-scope` values, or null when none or invalid. */
  scope: string | null;
  /** The scope's first level, or null. */
  area: string | null;
  /** The ARIA role, or the role the element implies (`button`, `link`, …). */
  component: string;
}

// Dropdowns count too: MUI Select and Autocomplete render role="combobox",
// and a native <select> is implicitly one (a click on its <option> lands on it).
const INTERACTIVE = [
  "button", "a[href]", "summary", "select", "input[type=checkbox]", "input[type=radio]",
  "[role=button]", "[role=menuitem]", "[role=tab]", "[role=option]",
  "[role=checkbox]", "[role=switch]", "[role=link]", "[role=combobox]",
].join(", ");

/** The `data-ph-scope` of every ancestor of `el`, outermost first. */
function scopesAbove(el: Element): string[] {
  const scopes: string[] = [];
  for (let parent = el.parentElement; parent; parent = parent.parentElement) {
    const scope = parent.getAttribute("data-ph-scope");
    if (scope) scopes.unshift(scope);
  }
  return scopes;
}

/**
 * Builds the tag for a click target (spec D6): the `data-ph-scope` of every
 * ancestor, outermost first, then the closest `data-ph`. Only the DOM is read,
 * so a portal (menu, dialog) has to declare its whole path in one scope
 * (spec D7). An invalid tag is dropped rather than sent half-right.
 */
export function buildTag(target: Element): Tag | null {
  const tagged = target.closest("[data-ph]");
  const element = tagged?.getAttribute("data-ph");
  if (!tagged || !element) return null;

  const tag = [...scopesAbove(tagged), element].join("-");
  if (!isValidTag(tag)) {
    if (import.meta.env.DEV) console.warn(`[analytics] invalid tag dropped: "${tag}"`);
    return null;
  }
  const levels = tag.split("-");
  return { tag, area: levels[0], action: levels[levels.length - 1], depth: levels.length, tag_source: "explicit" };
}

/**
 * The element's role if it is a known component role, else the role its tag
 * implies. A role attribute is free text (`role="remove secret.pdf"`), so only
 * listed values are ever sent (spec T1/T3).
 */
function componentOf(el: Element): string {
  const role = el.getAttribute("role");
  if (role && COMPONENT_ROLE_SET.has(role)) return role;
  const name = el.tagName.toLowerCase();
  if (name === "a") return "link";
  if (name === "select") return "combobox";
  if (name === "input") return (el as HTMLInputElement).type; // checkbox | radio, per INTERACTIVE
  return "button"; // <button> and <summary>
}

/**
 * Describes a click on an interactive element that has no `data-ph`, so the
 * coverage gap shows up in analytics (spec T3). Only the role and the scope
 * path are read: never text, aria-label or any other attribute, because
 * labels are often built from user data ("remove <filename>", spec T1).
 * Returns null for a click outside any interactive element.
 */
export function describeUntagged(target: Element): UntaggedClick | null {
  const el = target.closest(INTERACTIVE);
  if (!el) return null;
  const joined = scopesAbove(el).join("-");
  const scope = joined && isValidTag(joined) ? joined : null;
  return {
    tag: null,
    tag_source: "untagged",
    scope,
    area: scope ? scope.split("-")[0] : null,
    component: componentOf(el),
  };
}
